/**
 * O formulário do crédito no painel do dado (E207.F7.C01, owner, 01/11, GT-2026-0032): na linha da
 * conferência de identidade, o resultado da linha em cima (consistente numa divergência apurada é
 * o aceite com fundamento, E207.F7) e os valores lidos recolhidos atrás de "Corrigir um valor
 * lido"; um fundamento e um Salvar só, que diz o que falta em vez de ficar apagado. Fora da
 * conferência (o crédito sem linha), só a correção com fonte (E203.F4, ADR-0146 §7). O que se
 * grava e o que falta estão em `correcaoDoCredito`; as partes da tela, em `PartesDoCredito`.
 *
 * E203.F4.C (F-E203.F4-06): o crédito corrigido (o alvo) é escolhido à parte da fonte; com um só
 * crédito lido, ele já vem escolhido.
 */
import { useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import {
  RESULTADOS_DA_LINHA,
  vigenteEntre,
  type CampoDoCreditoLido,
  type CreditoNoPainelData,
  type ResultadoDaLinha,
} from '@/lib/revisaoDoDado';

import {
  camposDaLinha,
  chaveDoAlvo,
  edicaoInicial,
  gravarTudo,
  oQueFalta,
  oQueGrava,
  type Edicao,
  type EdicaoDoAlvo,
} from './correcaoDoCredito';
import { Falta } from './Gravacao';
import { Correcao, Resultado } from './PartesDoCredito';
import { Fundamento } from './Fundamento';
import { BotaoSalvar, useGravacao } from './Gravacao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado;

export interface FormularioDoCreditoProps extends PropsDoConteudo {
  creditos: readonly CreditoNoPainelData[];
  /** A linha da conferência de identidade (`identidade:<rótulo>`); `null` fora dela. */
  linha: string | null;
}

/** O alvo escolhido e a edição dele; ao mudar de alvo, a edição começa do lido do novo. */
function useEdicaoDoAlvo(
  creditos: readonly CreditoNoPainelData[],
  campos: readonly CampoDoCreditoLido[],
): EdicaoDoAlvo {
  const [escolhido, setEscolhido] = useState(creditos.length === 1 ? '0' : '');
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const alvo = escolhido === '' ? undefined : creditos[Number(escolhido)];
  let atual: Edicao | null = null;
  if (alvo !== undefined) {
    atual = edicao?.alvo === chaveDoAlvo(alvo) ? edicao : edicaoInicial(alvo, campos);
  }
  return { escolhido, setEscolhido, alvo, atual, setEdicao };
}

export function FormularioDoCredito({ creditos, linha, ...props }: FormularioDoCreditoProps) {
  const { pedido, mesa, acoes, onMudou } = props;
  const peloResultado = linha !== null;
  const campos = camposDaLinha(pedido.origem.chave);
  const impresso = vigenteEntre(RESULTADOS_DA_LINHA, pedido.origem.valor);
  const [resultado, setResultado] = useState<ResultadoDaLinha | ''>(impresso);
  const [corrigindo, setCorrigindo] = useState(!peloResultado);
  const edicao = useEdicaoDoAlvo(creditos, campos);
  const [fundamento, setFundamento] = useState('');
  const [falta, setFalta] = useState<string | null>(null);
  const gravavel = oQueGrava({ corrigindo, edicao, campos, peloResultado, resultado, impresso });

  const gravacao = useGravacao(
    () =>
      gravarTudo(props, { ...gravavel, edicao, fundamento: fundamento.trim(), linha, resultado }),
    () => {
      acoes.salvo();
      onMudou();
    },
  );

  function salvar(): void {
    const pendente = oQueFalta({
      ...gravavel,
      peloResultado,
      semAlvo: corrigindo && creditos.length > 1 && edicao.alvo === undefined,
      pagina: edicao.atual?.fonte.pagina,
      fundamento,
    });
    setFalta(pendente);
    if (pendente === null) void gravacao.salvar();
  }

  return (
    <div
      className="space-y-sc-3"
      data-testid="painel-credito"
      data-pelo-resultado={peloResultado ? 'true' : undefined}
    >
      <p className="text-body-sm text-fg-muted">
        {peloResultado ? t.linha.textoDoCredito : t.credito.texto}
      </p>
      {peloResultado ? <Resultado resultado={resultado} onResultado={setResultado} /> : null}
      <Correcao
        creditos={creditos}
        peloResultado={peloResultado}
        corrigindo={corrigindo}
        onCorrigindo={() => setCorrigindo((c) => !c)}
        edicao={edicao}
        campos={campos}
        mesa={mesa}
      />
      <Fundamento id="painel-credito-fundamento" valor={fundamento} onChange={setFundamento} />
      <BotaoSalvar
        estado={gravacao.estado}
        recusa={gravacao.recusa}
        onSalvar={salvar}
        testId="painel-credito-salvar"
      />
      <Falta falta={falta} estado={gravacao.estado} testId="painel-credito-falta" />
    </div>
  );
}
